import { useState, useRef } from "react";
import Select, { components } from "react-select";
import { BsXCircleFill } from "react-icons/bs";
import { FixedSizeList as List } from "react-window";

import { cn } from "../helpers/cn";

const DefaultItemHeight = 40; // 40px

const selectAllOption = {
  value: "<SELECT_ALL>",
  label: "Select all",
};

// const createOption = (label = "") => ({ value: label, label });

const DefaultMultiValueRemove = (props) => {
  return (
    <components.MultiValueRemove {...props}>
      <div className="btn btn-sm btn-outline-dark tw-bg-white hover:tw-text-red-700 hover:tw-bg-white hover:tw-border-red-700 muti-value-label">
        {props.data.label}
        <BsXCircleFill className="tw-mb-1 tw-ml-1" />
      </div>
    </components.MultiValueRemove>
  );
};

const DefaultMultiValueLabel = () => null;

const DefaultOptionWithCheckbox = (props) => (
  <components.Option
    {...props}
    className={cn(props?.className, "tw-m-0 tw-bg-red-500")}
  >
    <input
      className="tw-mr-2 tw-transition "
      type="checkbox"
      checked={props.isSelected}
      readOnly
      onChange={(event) => {
        event.preventDefault();
        event.stopPropagation();
      }}
    />
    <label>{props.data.label}</label>
  </components.Option>
);

const DefaultLimitTagsContainer = ({
  hasValue,
  children,
  getValue,
  ...props
}) => {
  if (!hasValue) {
    return (
      <components.ValueContainer {...props}>
        {children}
      </components.ValueContainer>
    );
  }

  const LIMIT_TAGS = 3;
  const [buttonTags, otherChildren] = children;
  const overflowCounter = getValue().length;
  const displayLimitButtonTags = buttonTags.slice(0, LIMIT_TAGS);

  return (
    <components.ValueContainer {...props}>
      {displayLimitButtonTags}
      {overflowCounter > LIMIT_TAGS && (
        <div className="tw-text-black tw-border tw-border-black tw-border-solid tw-p-1 tw-rounded">
          +{overflowCounter - LIMIT_TAGS}
        </div>
      )}
      {otherChildren}
    </components.ValueContainer>
  );
};

const DefaultCustomMenuList = (props) => {
  const { options, children, maxHeight, getValue, selectProps } = props;

  const { noOptionsMessage } = selectProps;

  const [value] = getValue();

  const initialOffset = options.indexOf(value) * DefaultItemHeight;

  const maxHeightOptions = () => {
    if (children.length <= 5) {
      return children.length * DefaultItemHeight + 5;
    }

    return maxHeight;
  };

  if (options.length === 0 || !Array.isArray(children)) {
    return (
      <div className="tw-p-2 tw-text-center tw-text-gray-500 tw-text-sm">
        {noOptionsMessage?.()}
      </div>
    );
  }

  return (
    <List
      height={maxHeightOptions()}
      itemCount={children?.length}
      itemSize={DefaultItemHeight}
      initialScrollOffset={initialOffset}
    >
      {({ index, style }) => (
        <div key={index} style={style}>
          {children[index]}
        </div>
      )}
    </List>
  );
};

/**
 * @param {import("react-select").Props & {
 *  options?: { value: string, label: string }[]
 *  freeSolo?: boolean
 *  isError?: boolean
 * }}
 */

function AutoCompleteReactSelect({
  value: currentValue = [],
  onChange: handleOnChange,
  freeSolo,
  isLoading,
  isError,
  options = [],
  components = {},
  ...props
}) {
  const [inputValue, setInputValue] = useState();
  const [isOpen, setIsOpen] = useState(false);
  // const [freeSoloOptions, setFreeSoloOptions] = useState([]);

  const valueRef = useRef(currentValue);
  valueRef.current = currentValue;

  const isAllOptionsSelected = () =>
    valueRef.current?.length === options.length;
  // const isAllOptionsSelected = () => {
  //   const optionsWithoutFreeSolo = options.filter(
  //     (option) => !freeSoloOptions.includes(option)
  //   );

  //   return valueRef.current?.length === optionsWithoutFreeSolo.length;
  // };

  const isOptionSelected = (option) =>
    valueRef.current.some(
      (currentValueRef) =>
        currentValueRef.value === option?.value || isAllOptionsSelected()
    );

  const getOptions = () => {
    if (isLoading || options?.length === 0) {
      return [];
    }

    return [selectAllOption, ...options];
  };

  const getValue = () => {
    if (isLoading) {
      return [];
    }

    if (isAllOptionsSelected() && options.length > 0) {
      return [selectAllOption];
    }

    return currentValue;
  };

  /**
   * @param {{value: string, label: string}} newValue
   */
  const onChange = (newValue, actionMeta) => {
    /**
     * @type { {action: string, option: {value: string, label: string}, removedValue: {value: string, label: string}} }
     */
    const { action, option, removedValue } = actionMeta;

    if (!handleOnChange) {
      return;
    }

    if (action === "select-option" && option.value === selectAllOption.value) {
      handleOnChange(options, actionMeta);
      return;
    }

    if (
      (action === "deselect-option" &&
        option.value === selectAllOption.value) ||
      (action === "remove-value" &&
        removedValue.value === selectAllOption.value)
    ) {
      handleOnChange([], actionMeta);
      return;
    }

    if (action === "deselect-option" && isAllOptionsSelected()) {
      handleOnChange(
        options?.filter(({ value }) => value !== option?.value),
        actionMeta
      );
      return;
    }

    handleOnChange(newValue || [], actionMeta);
  };

  // const handleKeyDown = (event) => {
  //   if (!freeSolo || !inputValue) return; // only handle freeSolo

  //   // Only allow freeSolo if the user press Enter, input value is not empty, and the user is not selecting an option
  //   // and the no option is available

  //   const currentInputValue = inputValue.toLowerCase().trim();

  //   // we dont want them free solo if there still option available

  //   // find value that already exist in option, this to prevent duplicate
  //   const availableOptions = getOptions().filter((option) =>
  //     option.label.toLowerCase().trim().includes(currentInputValue)
  //   );

  //   const isDuplicateValue = freeSoloOptions.some((option) =>
  //     option.label.toLowerCase().trim().includes(currentInputValue)
  //   ); // prevent duplicate

  //   switch (event.key) {
  //     case "Enter":
  //     case "Tab":
  //       if (availableOptions.length !== 0 || isDuplicateValue) {
  //         event.preventDefault();
  //         break;
  //       }

  //       setFreeSoloOptions((prev) => [
  //         ...prev,
  //         createOption(currentInputValue),
  //       ]);
  //       break;
  //     default:
  //       break;
  //   }
  // };

  const defaultComponents = {
    ClearIndicator: null,
    IndicatorSeparator: null,

    MultiValueLabel: DefaultMultiValueLabel,
    ValueContainer: DefaultLimitTagsContainer,
    MultiValueRemove: DefaultMultiValueRemove,
    Option: DefaultOptionWithCheckbox,
    ...components,
  };

  return (
    <Select
      components={
        isLoading
          ? defaultComponents
          : {
              ...defaultComponents,
              MenuList: DefaultCustomMenuList,
            }
      }
      noOptionsMessage={() => (isLoading ? "Loading..." : "No options")}
      {...props}
      isOptionSelected={isOptionSelected}
      options={getOptions()}
      value={getValue()}
      onChange={onChange}
      inputValue={inputValue}
      onInputChange={setInputValue}
      // onKeyDown={handleKeyDown}
      menuIsOpen={isOpen}
      onMenuOpen={() => setIsOpen(true)}
      onMenuClose={() => setIsOpen(false)}
      isMulti
      hideSelectedOptions={false}
      closeMenuOnSelect={false}
      styles={{
        multiValueRemove: () => ({}),
        valueContainer: (base) => ({
          ...base,
          display: "flex",
        }),
        input: () => ({
          visibility: "visible",
          margin: "2px",
          paddingTop: "2px",
          paddingBottom: "2px",
          ...(currentValue?.length === 0 && !isOpen
            ? {
                display: "inline-grid",
                gridArea: "1 / 1 / 2 / 3",
                gridTemplateColumns: "0px min-content",
              }
            : {
                width: "100%",
              }),
        }),
        control: (base) => ({
          ...base,
          boxShadow: isError ? "none" : base.boxShadow,
          borderColor: isError ? "red" : base.borderColor,
          ":hover": {
            borderColor: isError ? "red" : base.borderColor,
          },
        }),
      }}
    />
  );
}

export default AutoCompleteReactSelect;
